// Client helpers for cosmetic items rolled from boxes: resolve a SKU's display name + gacha rarity
// across all three catalogs (outfits, disks, sleeves), and project an item onto the player's saved
// look so PortraitPreview can render it ON the character (the box reveal + inventory preview).
import {
  findOutfit,
  findDuelDisk,
  duelDiskColorRarity,
  diskGachaRarity,
  sleeveGachaRarity,
  SLEEVE_RARITY,
  BETA_GOLD_DUELDISK_SKU,
  type CosmeticKind,
  type GachaRarity,
  type ChibiLook,
} from "@ygoevo/world-protocol";
import { SLEEVES } from "./duel/sleeves.ts";
import type { PortraitSelection } from "../render/PortraitAvatar.ts";

/** Mutually-exclusive avatar-slot partners (equipping one clears the other). */
const SLOT_PARTNER: Record<string, string> = { dc: "ac", ac: "dc", dd: "da", da: "dd" };

/** Human-readable name for a cosmetic SKU. */
export function cosmeticName(kind: CosmeticKind, sku: string): string {
  if (kind === "outfit") return findOutfit(sku)?.def.name ?? sku;
  if (kind === "dueldisk") {
    const e = findDuelDisk(sku);
    return e ? `${e.def.name}, ${e.c.name ?? `Colour ${e.c.color}`}` : sku;
  }
  if (kind === "sleeve") return SLEEVES.find((s) => s.id === sku)?.name ?? sku;
  return sku;
}

/** The grant-only Beta Gold Duel Disk, gets its own "Beta Exclusive" tag instead of a normal rarity. */
export function isBetaExclusive(kind: CosmeticKind, sku: string): boolean {
  return kind === "dueldisk" && sku === BETA_GOLD_DUELDISK_SKU;
}

/** Gacha rarity of a cosmetic SKU (for reveal glow + inventory tags). */
export function cosmeticRarity(kind: CosmeticKind, sku: string): GachaRarity {
  if (kind === "outfit") return findOutfit(sku)?.def.rarity ?? "common";
  if (kind === "dueldisk") {
    const e = findDuelDisk(sku);
    return e ? diskGachaRarity(duelDiskColorRarity(e.def, e.c)) : "common";
  }
  if (kind === "sleeve") return sleeveGachaRarity(SLEEVE_RARITY[sku] ?? "rare");
  return "common";
}

/** True for items PortraitPreview can render on the body (outfits/costumes + disks). Sleeves are
 *  card-backs, not avatar parts, callers show a SleeveSwatch instead. */
export function isWearable(kind: CosmeticKind): boolean {
  return kind === "outfit" || kind === "dueldisk";
}

/** Project a wearable cosmetic onto the player's saved look, so PortraitPreview shows it ON the
 *  character. Clears the mutually-exclusive partner slot. Returns the base unchanged for sleeves. */
export function lookWithCosmetic(base: ChibiLook, kind: CosmeticKind, sku: string): { gender: "cm" | "cw"; selection: PortraitSelection } {
  const selection: PortraitSelection = structuredClone(base.slots ?? {});
  const e = kind === "outfit" ? findOutfit(sku) : kind === "dueldisk" ? findDuelDisk(sku) : null;
  if (e) {
    selection[e.def.slot] = { variant: e.def.variant, color: e.c.color };
    const partner = SLOT_PARTNER[e.def.slot];
    if (partner) selection[partner] = { variant: "00", color: 1 };
  }
  return { gender: base.gender, selection };
}
